import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const SoundContext = createContext(null);

// Simple beep sounds using Web Audio API - no external files needed
function createBeep(audioContext, frequency, duration, volume = 0.1) {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.frequency.value = frequency;
  oscillator.type = 'sine';
  
  gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
}

// Sound definitions using frequencies for different UI feedback
const SOUND_CONFIGS = {
  click: { frequency: 800, duration: 0.05, volume: 0.08 },
  hover: { frequency: 600, duration: 0.03, volume: 0.04 },
  success: { frequency: 1200, duration: 0.1, volume: 0.1 },
  whoosh: { frequency: 400, duration: 0.15, volume: 0.06 },
  pop: { frequency: 1000, duration: 0.04, volume: 0.08 },
  magic: { frequency: 1400, duration: 0.2, volume: 0.08 },
};

export function SoundProvider({ children }) {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('ttc_sound_enabled');
    return stored === null ? true : stored === 'true';
  });
  const audioContextRef = useRef(null);

  // Initialize audio context on user interaction
  useEffect(() => {
    const initAudio = () => {
      if (!audioContextRef.current) {
        try {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
          console.warn('Web Audio API not supported');
        }
      }
    };

    // Initialize on first interaction
    const handleInteraction = () => {
      initAudio();
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
    };

    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  // Persist preference
  useEffect(() => {
    localStorage.setItem('ttc_sound_enabled', soundEnabled.toString());
  }, [soundEnabled]);

  const playSound = useCallback((soundName) => {
    if (!soundEnabled || !audioContextRef.current) return;
    
    const config = SOUND_CONFIGS[soundName];
    if (!config) return;

    try {
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      createBeep(
        audioContextRef.current, 
        config.frequency, 
        config.duration, 
        config.volume
      );
    } catch (e) {
      // Silently fail
    }
  }, [soundEnabled]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => !prev);
  }, []);

  return (
    <SoundContext.Provider value={{ playSound, soundEnabled, toggleSound }}>
      {children}
    </SoundContext.Provider>
  );
}

export function useSound() {
  const context = useContext(SoundContext);
  if (!context) {
    return {
      playSound: () => {},
      soundEnabled: false,
      toggleSound: () => {},
    };
  }
  return context;
}
